import React from "react";
import { Button, Form, Input, InputNumber, Select, Space, Switch, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

const { Text } = Typography;

export interface ToolBinding {
  type: string;
  enabled?: boolean;
  url?: string;
  projectId?: number;
  serverId?: number;
  toolName?: string;
  [key: string]: any;
}

const TOOL_TYPES = [
  { value: "knowledge_search", label: "知识库检索(knowledge_search)" },
  { value: "get_current_time", label: "当前时间(get_current_time)" },
  { value: "http_request", label: "HTTP 请求(http_request)" },
  { value: "mcp", label: "MCP 工具" },
];

interface ToolBindingPanelProps {
  value?: ToolBinding[];
  onChange?: (value: ToolBinding[]) => void;
  knowledgeOptions?: { label: string; value: number }[];
}

/**
 * ToolBindingPanel 统一工具绑定编辑(PRD v4 08§4 / 05§5):
 * 以 ToolBinding 结构编辑 tools_config,旧散落字段(serverId/toolName/url/projectId)按类型展示。
 * 执行链路由后端 Tool Registry 统一(toolregistry.WrapAll),本组件只管声明。
 */
const ToolBindingPanel: React.FC<ToolBindingPanelProps> = ({ value = [], onChange, knowledgeOptions = [] }) => {
  const update = (index: number, patch: Partial<ToolBinding>) => {
    const next = value.map((item, i) => (i === index ? { ...item, ...patch } : item));
    onChange?.(next);
  };

  const remove = (index: number) => {
    onChange?.(value.filter((_, i) => i !== index));
  };

  const add = () => {
    onChange?.([...value, { type: "knowledge_search", enabled: true }]);
  };

  return (
    <div>
      {value.length === 0 && (
        <Text type="secondary">尚未绑定工具。绑定后模型可在对话中自主调用(支持 function calling 的模型生效,否则自动降级为前置注入)。</Text>
      )}
      {value.map((tool, index) => (
        <div
          key={index}
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: 12,
            marginBottom: 8,
          }}
        >
          <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
            <Space wrap>
              <Select
                style={{ minWidth: 240 }}
                value={tool.type || tool.name}
                options={TOOL_TYPES}
                onChange={(type) => update(index, { type })}
              />
              <Form.Item label="启用" style={{ marginBottom: 0 }}>
                <Switch checked={tool.enabled !== false} onChange={(enabled) => update(index, { enabled })} />
              </Form.Item>
            </Space>
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(index)} />
          </Space>
          <div style={{ marginTop: 8 }}>
            {(tool.type === "http_request" || tool.name === "http_request") && (
              <Input
                placeholder="https:// 开头的数据源地址"
                value={tool.url}
                onChange={(e) => update(index, { url: e.target.value })}
              />
            )}
            {(tool.type === "knowledge_search" || tool.name === "knowledge_search") && (
              <Select
                style={{ minWidth: 280 }}
                placeholder="检索的知识库(留空=Agent 绑定的第一个)"
                allowClear
                value={tool.projectId}
                options={knowledgeOptions}
                onChange={(projectId) => update(index, { projectId })}
              />
            )}
            {(tool.type === "mcp" || tool.type === "mcp_tool" || tool.name === "mcp") && (
              <Space wrap>
                <InputNumber
                  placeholder="MCP 服务器ID"
                  min={1}
                  value={tool.serverId}
                  onChange={(serverId) => update(index, { serverId: serverId ?? undefined })}
                />
                <Input
                  placeholder="工具名(留空=桥接该服务器全部启用工具)"
                  style={{ width: 280 }}
                  value={tool.toolName}
                  onChange={(e) => update(index, { toolName: e.target.value })}
                />
              </Space>
            )}
          </div>
        </div>
      ))}
      <Button icon={<PlusOutlined />} onClick={add}>
        添加工具
      </Button>
    </div>
  );
};

export default ToolBindingPanel;
